import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import { useHTTP } from './useHttp';

// API access for the call protocols staff fill in while a call runs.
export const useCallProtocol = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, setError, sendRequest } = useHTTP();

  const getProtocol = useCallback(
    async protocolId => {
      try {
        return await sendRequest({
          url: `/api/v1/callProtocol/${protocolId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (e) {
        toast.error('Fehler beim Laden des Gesprächsprotokolls');
        throw e;
      }
    },
    [authCtx, sendRequest]
  );

  const getProtocolByCall = useCallback(
    async callId => {
      try {
        return await sendRequest({
          url: `/api/v1/callProtocol/byCall/${callId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (e) {
        // A call without a protocol is a normal state, not an error worth a toast.
        throw e;
      }
    },
    [authCtx, sendRequest]
  );

  const listProtocols = useCallback(
    async ({ status, caller, from, to, limit = 50, skip = 0 } = {}) => {
      try {
        const params = { limit, skip };
        if (status) params.status = status;
        if (caller) params.caller = caller;
        if (from) params.from = from;
        if (to) params.to = to;
        return await sendRequest({
          url: '/api/v1/callProtocol',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params,
        });
      } catch (e) {
        toast.error('Fehler beim Laden der Gesprächsprotokolle');
        throw e;
      }
    },
    [authCtx, sendRequest]
  );

  const getOptions = useCallback(async () => {
    try {
      return await sendRequest({
        url: '/api/v1/callProtocol/options',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (e) {
      toast.error('Fehler beim Laden der Protokoll-Auswahllisten');
      throw e;
    }
  }, [authCtx, sendRequest]);

  const createProtocol = useCallback(
    async callId => {
      try {
        return await sendRequest({
          url: '/api/v1/callProtocol',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { callId },
        });
      } catch (e) {
        toast.error('Fehler beim Anlegen des Gesprächsprotokolls');
        throw e;
      }
    },
    [authCtx, sendRequest]
  );

  const updateProtocol = useCallback(
    async (protocolId, payload) => {
      try {
        return await sendRequest({
          url: `/api/v1/callProtocol/${protocolId}`,
          method: 'PATCH',
          headers: authCtx.getAuthHeaders(),
          body: payload,
        });
      } catch (e) {
        toast.error('Fehler beim Speichern des Gesprächsprotokolls');
        throw e;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    isLoading,
    error,
    setError,
    getProtocol,
    getProtocolByCall,
    listProtocols,
    getOptions,
    createProtocol,
    updateProtocol,
  };
};

export default useCallProtocol;
